/**
 * The last line before a blank screen.
 *
 * There was no error boundary in this app until 2026-07-27, and the consequence
 * was not theoretical: the route views are behind `lazy()` (`App.tsx`), so a
 * chunk the current deployment no longer served took the entire tree down and
 * left the user looking at nothing at all. Nothing to read, nothing to tap, and
 * no way to tell a stale cache apart from a dead app.
 *
 * INV-7 — fail loud, but loud is not fatal. A stale build recovers itself, once
 * (`recovery.ts`). Anything else is *named on screen*, because an error the user
 * can read is an error they can report.
 *
 * D-1: no cards. D-7: the voice is allowed here — an error state is exactly
 * where nothing is at stake and we have the user's attention — but the control
 * label stays plain working copy.
 */

import { Engraving } from "@/features/common/engraving";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { PrimaryButton } from "./primitives";
import { clearBuildCaches, isStaleBuildError, recoverFromStaleBuild } from "@/lib/recovery";

interface State {
  error: Error | null;
  /** True while the stale-build reload is in flight, so we show no error text. */
  recovering: boolean;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, recovering: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, recovering: isStaleBuildError(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only telemetry this app has, and it is deliberately the
    // only one — there is no analytics endpoint here and adding one would be a
    // decision, not a detail. Keep the component stack: it is what makes a
    // user-reported screenshot actionable.
    console.error("Unhandled error, caught at the app boundary:", error, info.componentStack);

    if (isStaleBuildError(error)) {
      void recoverFromStaleBuild().then(() => {
        // Only reached when recovery was declined by the latch — a stale-build
        // error that survived its own reload. Show it rather than spin.
        this.setState({ recovering: false });
      });
    }
  }

  private readonly reload = (): void => {
    void clearBuildCaches().finally(() => {
      location.reload();
    });
  };

  override render(): ReactNode {
    const { error, recovering } = this.state;
    if (!error) return this.props.children;

    // The reload is already on its way; a flash of error copy would be a lie.
    if (recovering) return null;

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-phi-2 bg-bg px-phi-3 text-center">
        <Engraving name="storm-water" className="w-full max-w-phi-12" />
        <div className="flex flex-col gap-phi-n1">
          <h1 className="text-phi-1 font-semibold tracking-tight">The tide went out</h1>
          <p className="max-w-phi-12 text-phi-n1 text-tx-2">
            Something broke on the way to the surface. Your ledger lives in your account, and
            anything not yet uploaded is saved on this device — nothing here touches either.
          </p>
        </div>

        {/* The message, verbatim. A user who can read it can report it, and a
            boundary that hides the cause is only a prettier blank screen. */}
        <p className="max-w-phi-13 break-words font-mono text-phi-n1 text-tx-2 opacity-70">
          {error.message}
        </p>

        <PrimaryButton onClick={this.reload} className="max-w-phi-13">
          Reload
        </PrimaryButton>
      </div>
    );
  }
}
